import { inputLogger } from '../Logger';

import { Entity, Position, Rectangle, QueryOptions } from './CollisionTypes';

/**
 * Spatial hash grid for efficient collision queries
 * Divides the world into cells and tracks entities in each cell
 */
export class SpatialGrid {
  private grid: Map<string, Set<Entity>> = new Map();
  private entityCells: Map<string, Set<string>> = new Map(); // Track which cells each entity is in
  private cellSize: number;

  constructor(cellSize: number = 16) {
    this.cellSize = cellSize;
  }

  /**
   * Get the grid cell key for a position
   */
  private getCellKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.cellSize);
    const cellY = Math.floor(y / this.cellSize);
    return `${cellX},${cellY}`;
  }

  /**
   * Get all cell keys that a rectangle overlaps
   */
  private getCellKeysForBounds(bounds: Rectangle): string[] {
    const startX = Math.floor(bounds.x / this.cellSize);
    const startY = Math.floor(bounds.y / this.cellSize);
    const endX = Math.floor((bounds.x + bounds.width - 1) / this.cellSize);
    const endY = Math.floor((bounds.y + bounds.height - 1) / this.cellSize);

    const cells: string[] = [];
    for (let x = startX; x <= endX; x++) {
      for (let y = startY; y <= endY; y++) {
        cells.push(`${x},${y}`);
      }
    }
    return cells;
  }

  /**
   * Insert an entity into the spatial grid
   */
  insert(entity: Entity): void {
    this.remove(entity); // Remove first to avoid duplicates

    const cellKeys = this.getCellKeysForBounds(entity.bounds);
    const entityCells = new Set<string>();

    for (const cellKey of cellKeys) {
      // Add entity to cell
      if (!this.grid.has(cellKey)) {
        this.grid.set(cellKey, new Set());
      }
      const cell = this.grid.get(cellKey);
      if (cell) {
        cell.add(entity);
      }
      entityCells.add(cellKey);
    }

    // Track which cells this entity is in
    this.entityCells.set(entity.id, entityCells);

    inputLogger.debug('Entity inserted into spatial grid', {
      entityId: entity.id,
      type: entity.type,
      cellCount: cellKeys.length,
      cells: cellKeys,
    });
  }

  /**
   * Remove an entity from the spatial grid
   */
  remove(entity: Entity): void {
    const entityCells = this.entityCells.get(entity.id);
    if (!entityCells) return;

    // Remove entity from all cells it was in
    for (const cellKey of Array.from(entityCells)) {
      const cell = this.grid.get(cellKey);
      if (cell) {
        cell.delete(entity);
        // Clean up empty cells
        if (cell.size === 0) {
          this.grid.delete(cellKey);
        }
      }
    }

    // Remove entity tracking
    this.entityCells.delete(entity.id);

    inputLogger.debug('Entity removed from spatial grid', {
      entityId: entity.id,
      cellCount: entityCells.size,
    });
  }

  /**
   * Update an entity's position in the spatial grid
   */
  update(entity: Entity): void {
    this.insert(entity); // Insert handles removal first
  }

  /**
   * Query entities at a specific position
   */
  queryPosition(x: number, y: number, options: QueryOptions = {}): Entity[] {
    const cellKey = this.getCellKey(x, y);
    const cell = this.grid.get(cellKey);
    if (!cell) return [];

    return this.filterEntities(Array.from(cell), options).filter(entity =>
      this.pointInBounds({ x, y }, entity.bounds)
    );
  }

  /**
   * Query entities within a radius of a position
   */
  queryRadius(
    center: Position,
    radius: number,
    options: QueryOptions = {}
  ): Entity[] {
    const bounds: Rectangle = {
      x: center.x - radius,
      y: center.y - radius,
      width: radius * 2,
      height: radius * 2,
    };

    const entities = this.queryRectangle(bounds, options);

    // Filter by actual distance
    return entities.filter(entity => {
      const distance = Math.sqrt(
        Math.pow(entity.position.x - center.x, 2) +
          Math.pow(entity.position.y - center.y, 2)
      );
      return distance <= radius;
    });
  }

  /**
   * Query entities within a rectangular area
   */
  queryRectangle(bounds: Rectangle, options: QueryOptions = {}): Entity[] {
    const cellKeys = this.getCellKeysForBounds(bounds);
    const entitiesSet = new Set<Entity>();

    // Collect entities from all overlapping cells
    for (const cellKey of cellKeys) {
      const cell = this.grid.get(cellKey);
      if (cell) {
        for (const entity of Array.from(cell)) {
          if (this.boundsOverlap(bounds, entity.bounds)) {
            entitiesSet.add(entity);
          }
        }
      }
    }

    return this.filterEntities(Array.from(entitiesSet), options);
  }

  /**
   * Get all entities (for debugging)
   */
  getAllEntities(): Entity[] {
    const entitiesSet = new Set<Entity>();
    for (const cell of Array.from(this.grid.values())) {
      for (const entity of Array.from(cell)) {
        entitiesSet.add(entity);
      }
    }
    return Array.from(entitiesSet);
  }

  /**
   * Clear all entities from the grid
   */
  clear(): void {
    this.grid.clear();
    this.entityCells.clear();
    inputLogger.debug('Spatial grid cleared');
  }

  /**
   * Get grid statistics for debugging
   */
  getStats(): {
    totalCells: number;
    totalEntities: number;
    averageEntitiesPerCell: number;
  } {
    const totalCells = this.grid.size;
    const allEntities = this.getAllEntities();
    const totalEntities = allEntities.length;
    const averageEntitiesPerCell =
      totalCells > 0 ? totalEntities / totalCells : 0;

    return { totalCells, totalEntities, averageEntitiesPerCell };
  }

  /**
   * Helper: Check if a point is within bounds
   */
  private pointInBounds(point: Position, bounds: Rectangle): boolean {
    return (
      point.x >= bounds.x &&
      point.x < bounds.x + bounds.width &&
      point.y >= bounds.y &&
      point.y < bounds.y + bounds.height
    );
  }

  /**
   * Helper: Check if two rectangles overlap
   */
  private boundsOverlap(bounds1: Rectangle, bounds2: Rectangle): boolean {
    return (
      bounds1.x < bounds2.x + bounds2.width &&
      bounds1.x + bounds1.width > bounds2.x &&
      bounds1.y < bounds2.y + bounds2.height &&
      bounds1.y + bounds1.height > bounds2.y
    );
  }

  /**
   * Helper: Filter entities based on query options
   */
  private filterEntities(entities: Entity[], options: QueryOptions): Entity[] {
    let filtered = entities;

    if (options.layers) {
      const layers = options.layers;
      filtered = filtered.filter(entity =>
        layers.includes(entity.collisionLayer)
      );
    }

    if (options.entityTypes) {
      const entityTypes = options.entityTypes;
      filtered = filtered.filter(entity => entityTypes.includes(entity.type));
    }

    if (options.includeSolid === false) {
      filtered = filtered.filter(entity => !entity.isSolid);
    }

    if (options.includeStatic === false) {
      filtered = filtered.filter(entity => !entity.isStatic);
    }

    return filtered;
  }
}
